import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { IFieldDefinition, IWorkflowBaseRecord, IWorkflowNodeRequestConfig } from "../interfaces";
import { RecordCard } from "./RecordCard";

interface IRelationRecordListProps {
  title: string;
  records: IWorkflowBaseRecord[];
  selectedRecordUid: string | null;
  onSelectRecord: (uid: string) => void;
  loading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  fields?: IFieldDefinition<any>[];
  binding?: {
    key: string;
    requestConfig: IWorkflowNodeRequestConfig;
  };
  width?: string | number;
  emptyMessage?: string;
  loadingMessage?: string;
  droppableId?: string;
  draggableSource?: "used" | "available";
}

interface IDraggableRelationRecordCardProps {
  id: string;
  source: "used" | "available";
  record: IWorkflowBaseRecord;
  children: ReactNode;
}

const DraggableRelationRecordCard = (props: IDraggableRelationRecordCardProps) => {
  const { id, source, record, children } = props;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: {
      source,
      record,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.6 : 1,
    cursor: "grab",
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </Box>
  );
};

export const RelationRecordList = (props: IRelationRecordListProps) => {
  const {
    title,
    records,
    selectedRecordUid,
    onSelectRecord,
    loading = false,
    error = null,
    hasMore = false,
    isFetchingMore = false,
    onLoadMore,
    fields = [],
    binding,
    width = "40%",
    emptyMessage = "No records found.",
    loadingMessage = "Loading records...",
    droppableId = "used-list",
    draggableSource = "used",
  } = props;

  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
    id: droppableId,
  });

  const getDisplayRecord = (record: IWorkflowBaseRecord): IWorkflowBaseRecord => {
    if (!binding?.key) {
      return record;
    }

    const recordMap = record as unknown as Record<string, unknown>;
    const nestedRecord = recordMap[binding.key];

    if (!nestedRecord || typeof nestedRecord !== "object") {
      return record;
    }

    return nestedRecord as IWorkflowBaseRecord;
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const threshold = 80;

    const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    if (isNearBottom && hasMore && !isFetchingMore && onLoadMore) {
      onLoadMore();
    }
  };

  return (
    <Box
      sx={{
        width,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        overflow: "hidden",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: isOver ? "primary.main" : "divider",
          boxShadow: isOver ? 2 : 0,
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
        </Box>

        <Box
          ref={setDroppableNodeRef}
          onScroll={handleScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            borderRadius: 2,
          }}
        >
          {loading ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {loadingMessage}
              </Typography>
            </Box>
          ) : null}

          {!loading && error ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          ) : null}

          {!loading && !error && records.length === 0 ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {emptyMessage}
              </Typography>
            </Box>
          ) : null}

          {!loading && !error && records.length > 0 ? (
            <Stack spacing={1.5} sx={{ pb: isFetchingMore ? 1 : 0 }}>
              {records.map((record) => {
                const displayRecord = getDisplayRecord(record);

                return (
                  <DraggableRelationRecordCard key={record.uid} id={`${draggableSource}-${record.uid}`} source={draggableSource} record={record}>
                    <RecordCard
                      title={displayRecord.name ?? record.name ?? record.uid}
                      subtitle={displayRecord.description ?? record.description}
                      imageUrl={displayRecord.imageUrl ?? record.imageUrl}
                      record={displayRecord}
                      fields={fields}
                      selected={selectedRecordUid === record.uid}
                      onClick={() => onSelectRecord(record.uid)}
                    />
                  </DraggableRelationRecordCard>
                );
              })}
            </Stack>
          ) : null}
        </Box>

        {isFetchingMore ? (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};
